import { useEffect, useState } from "react";
import DocumentTitle from "react-document-title";
import CompetitionsService from "../../services/CompetitionsService";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import styles from './index.module.sass';
import BackButton from "../../components/buttons/BackButton";
import { useHistory, useLocation } from "react-router";
import ContractDocsService, { ContractDocsVersionType } from "../../services/ContractDocsService";
import { ErrorModal } from "../../components/Modals/ErrorModal";
import { ISignature } from "../../services/SignaturesService";
import { UserType } from "../../services/UserService";
import Auth from "../../Auth/Auth";
import moment from "moment";
import PaymentService, { PaymentType } from "../../services/PaymentService";
import InespayService from "../../services/InespayService";
import { SignatureComponentWithUserNoUserIdentity } from "../../components/Signature/SignatureComponentWithUserNoUserIdentity";
import { createurl } from "../../utils/createUrl";
import { parseInespayResult, isInespaySuccess } from '@/utils/InespayResponse';

const CompetitionContractNoActivePlan = () =>
{
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState({ status: false, message: "" });
    const [sending, setSending] = useState<boolean>(false);
    const [canceling, setCanceling] = useState<boolean>(false);
    const location = useLocation();
    const history = useHistory();
    const user = Auth.getUserProfile();
    // El estado de navegación no existe al recargar o entrar por enlace directo: se lee sin desestructurar para no lanzar un TypeError
    const state = location?.state as { competition?: any, competitionUserObject?: any, fromEmail?: boolean } | undefined;
    const competition = state?.competition;
    const competitionUserObject = state?.competitionUserObject;
    const [contractHTML, setContractHTML] = useState<string>('');
    // Contrato no disponible (sin contrato asociado o error al cargarlo): no se permite firmar un contrato que no se ha mostrado
    const [contractUnavailable, setContractUnavailable] = useState<boolean>(false);
    // El pago posterior a la firma ha fallado: se deja de mostrar el mensaje de espera
    const [paymentFailed, setPaymentFailed] = useState<boolean>(false);
    const [signatureCompetition, setSignatureCompetition] = useState<ISignature>();
    const [phoneNumber, setPhoneNumber] = useState("");
    const [finalUserInCompetition, setFinalUserInCompetition] = useState<UserType>();
    const [signatureId, setSignatureId] = useState<number>(-1);

    useEffect(() =>
    {
        // Sin datos de navegación se muestra el aviso de más abajo
        if(competition === undefined || competitionUserObject === undefined)
        {
            setLoading(false);
            return;
        }

        // Sin contrato asociado no se pide /contractDocs/version/undefined
        if(competition.asociatedContractId === undefined || competition.asociatedContractId === null)
        {
            setContractUnavailable(true);
            setError({ status: true, message: "La competición no tiene un contrato asociado. Por favor, contacta con info@ironia.tech." });
            setLoading(false);
            return;
        }

        ContractDocsService.getContractVersionById(competition.asociatedContractId).then((contractDocsVersion: ContractDocsVersionType) =>
        {
            setContractHTML(contractDocsVersion?.text ?? '');
        })
        .catch((error: any) =>
        {
            setContractUnavailable(true);
            setError({ status: true, message: (typeof error?.response?.data === "string" && error.response.data) || "No se ha podido cargar el contrato de la competición. Por favor, inténtalo de nuevo más tarde." });
        })
        .finally(() =>
        {
            setLoading(false);
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() =>
    {
        if(signatureId !== -1 && state?.fromEmail === true)
        {
            history.push(`/competition/${createurl(competition.name)}-${competition.id}`, { competitionId: competition.id });
        }
        else
        {
            if(signatureId !== -1 && competition !== undefined && signatureCompetition !== undefined && finalUserInCompetition !== undefined)
            {
                //Puede ser CASO A o CASO C
                // A. € - nuestra plataforma - SI contrato ===> contrato - firma (SMS) - inespay
                // C. € - OTRA plataforma - SI contrato    ===> contrato - firma (SMS) - url

                if(competition?.typeOfPrice === 3)
                {
                    console.error("La competición por pago externo no debe incluir un contrato asociado.");
                    setPaymentFailed(true);
                    return setError({ status: true, message: "Se ha producido un error con la competición. Porfavor, contacta con info@ironia.tech." });

                }
                else
                {
                    //CASO A
                    var reference = `${user.id}-O${signatureCompetition.id}`;
                    let subject = `Competición IronIA - ${finalUserInCompetition.id} - ${user.id}`;

                    const data =
                    {
                        reference: reference,
                        subject: subject,
                        amount: competition.price,
                        status: 1,
                        platform: "INESPAY",
                        orders: undefined,
                        usersSubscriptionId: undefined,
                        savingsPlanId: competitionUserObject.savingsPlanId
                    }

                    //en este paso, 
                    PaymentService.createPaymentFromCompetition(data, finalUserInCompetition.id!).then((data: Partial<PaymentType>) =>
                    {
                        InespayService.postInespay(
                        {
                            reference: reference,
                            subject: subject,
                            amount: ((competition.price) * 100).toString(),
                            subscription: false
                        }).then((dataInespay: any) =>
                        {
                            const inespay_result = parseInespayResult(dataInespay);
                            if(inespay_result === null)
                            {
                                console.error('postInespay: respuesta inválida', dataInespay);
                                // Antes solo se registraba en consola y el usuario se quedaba esperando indefinidamente
                                setPaymentFailed(true);
                                setError({ status: true, message: "No se ha podido iniciar el pago de la competición. Por favor, contacta con info@ironia.tech." });
                                return;
                            }

                            if(isInespaySuccess(inespay_result) && inespay_result.url)
                            {
                                window.location.href = inespay_result.url;
                            }
                            else
                            {
                                if(inespay_result.status === "E301")
                                {
                                    console.log("El IBAN introducido no es válido.");
                                }
                                else
                                {
                                    console.log(inespay_result.description);
                                }
                                setPaymentFailed(true);
                                setError(
                                {
                                    status: true,
                                    message: inespay_result.status === "E301"
                                        ? "No se ha podido iniciar el pago: el IBAN introducido no es válido. Por favor, contacta con info@ironia.tech."
                                        : "No se ha podido iniciar el pago de la competición. Por favor, contacta con info@ironia.tech."
                                });
                            }
                        },
                        (error: any) =>
                        {
                            setPaymentFailed(true);
                            setError({ status: true, message: "Se ha producido un error con la competición. Porfavor, contacta con info@ironia.tech." });
                        });
                    },
                    (error: any) =>
                    {
                        setPaymentFailed(true);
                        setError({ status: true, message: "Se ha producido un error con la competición. Porfavor, contacta con info@ironia.tech." });
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signatureId]);

    const processExpirationDate = (signature: ISignature) =>
    {
        if(signature && typeof signature.expiresAt === "string")
        {
            signature.expiresAt = moment(signature.expiresAt).toDate();
        }
    };

    const cancelParticipation = () =>
    {
        setCanceling(true);
        CompetitionsService.inscribeInCompetitionStatusCancel(competitionUserObject.id).then((data) =>
        {
            if(data === true)
            {
                history.push("/competitions-promotioned")
            }
            else
            {
                setCanceling(false);
                setError({ status: true, message: (typeof data === "string" && data) || "No se ha podido cancelar tu participación. Por favor, inténtalo de nuevo más tarde." })
            }
        })
        .catch((error: any) =>
        {
            // Antes el botón Cancelar se quedaba bloqueado con el spinner
            setCanceling(false);
            setError({ status: true, message: (typeof error?.response?.data === "string" && error.response.data) || "No se ha podido cancelar tu participación. Por favor, inténtalo de nuevo más tarde." });
        })
    }

    const createSign = async () =>
    {
        setSending(true);
        try
        {
            // Sin teléfono no se puede enmascarar el número: se muestra vacío en lugar de lanzar
            const phone: string = competitionUserObject?.user?.phone ?? "";
            const maskedPhone = phone.length >= 3 ? "X".repeat(phone.length - 3) + phone.slice(phone.length - 3) : phone;

            if(state?.fromEmail === true)
            {
                const createSignature = await CompetitionsService.patchSignatureWithEmail(competitionUserObject.id, user.email);

                setFinalUserInCompetition(createSignature);
                setSignatureCompetition(createSignature?.signature);
                setPhoneNumber(maskedPhone);
            }
            else
            {
                const createSignature = await CompetitionsService.patchSignature(competitionUserObject.id);

                setFinalUserInCompetition(createSignature);
                setSignatureCompetition(createSignature?.signature);
                setPhoneNumber(maskedPhone);
            }
        }
        catch (error: any)
        {
            // Sin respuesta del servidor (red, timeout) error.response no existe
            setError({ status: true, message: (typeof error?.response?.data === "string" && error.response.data) || "No se ha podido generar la firma. Por favor, inténtalo de nuevo más tarde." });
        }
        finally
        {
            setSending(false);
        }
    }

    const handleSubmit = (response: any) =>
    {
        if(response.data.success === true && signatureCompetition !== undefined && signatureCompetition.id !== undefined)
        {
            setSignatureId(signatureCompetition.id);
        }
    };

    if(location === undefined || state === undefined || competition === undefined || competitionUserObject === undefined)
    {
        return(
            <DocumentTitle title="IronIA - Competiciones">
                <div>
                    <BackButton />
                    <div className="ms-Grid-row">
                        <div className="ms-Grid-col ms-sm12">
                            <h2>Competiciones</h2>
                        </div>
                    </div>
                    <p style={{marginTop: "20px"}}>Error: No hemos podido procesar tu petición. Porfavor, vuelve a intentarlo.</p>
                    <DefaultButton className="button tiny-primary-button" onClick={() => history.push("/competitions-promotioned")}>
                        Volver a competiciones
                    </DefaultButton>
                </div>
            </DocumentTitle>
        );
    }

    return(
        <DocumentTitle title="IronIA - Competiciones">
            <section className={`${styles.index}`}>
                <BackButton />
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12">
                        <h2>Competiciones</h2>
                    </div>
                </div>
                {loading && <Spinner size={SpinnerSize.large} />}
                {!loading && <>
                    <div dangerouslySetInnerHTML={{ __html: contractHTML }}></div>
                    <div style={{display: "flex", justifyContent: "right", marginTop: "30px"}}>
                        <DefaultButton
                            className="button tiny-primary-button"
                            onClick={cancelParticipation}
                            disabled={canceling}
                        >
                            {canceling &&
                            (
                                <Spinner size={SpinnerSize.small} className="button-spinner" />
                            )}
                            Cancelar
                        </DefaultButton>
                        <DefaultButton
                            className="button tiny-primary-button"
                            type="submit"
                            disabled={sending || signatureCompetition !== undefined || contractUnavailable}
                            onClick={createSign}
                        >
                            {sending &&
                            (
                                <Spinner size={SpinnerSize.small} className="button-spinner" />
                            )}
                            Participar
                        </DefaultButton>
                    </div>
                    {(signatureCompetition !== undefined || signatureCompetition != null) && finalUserInCompetition !== undefined && 
                        <SignatureComponentWithUserNoUserIdentity
                            signatures={[signatureCompetition]}
                            title="Participar en la competición"
                            submitButtonText="Firmar"
                            requestNewCodeButtonText="Recibir nuevo código"
                            headerTextArea={`Confirma la aceptación con la clave enviada a tu teléfono ${phoneNumber}.`}
                            onBackButtonClicked={cancelParticipation}
                            onCancelButtonClicked={cancelParticipation}
                            users={[finalUserInCompetition]}
                            cancelButtonText="Cancelar"
                            alreadySignedText=
                            {
                                paymentFailed
                                    ? "La firma ha sido guardada, pero no se ha podido completar el proceso. Por favor, contacta con info@ironia.tech."
                                    : "La firma ha sido guardada. Espere mientras procesamos la información..."
                            }
                            onNewCodeSended={(signatures: any) =>
                            {
                                signatures.forEach((signature: any) =>
                                {
                                    processExpirationDate(signature);
                                });
                                setSignatureCompetition(signatures[0]);
                            }}
                            onSignatureChanged={(signature: any) =>
                            {
                                processExpirationDate(signature);
                                setSignatureCompetition(signature);
                            }}
                            onSigned={(response: any, signatures: any) =>
                            {
                                processExpirationDate(signatures);
                                //setSignatureCompetition(signatures);
                                handleSubmit(response);
                            }}
                        />
                    }
                    <ErrorModal title="Ha ocurrido un error al procesar tu solicitud"
                        body={<div><p>{error.message}</p></div>}
                        btnSubmitText="Aceptar"
                        showModal={error.status}
                        onSubmit={() =>
                        {
                            setError({ status: false, message: "" });
                        }}
                        onClose={() =>
                        {
                            setError({ status: false, message: "" });
                        }}
                    /></>
                }
            </section>
        </DocumentTitle>
    );
};

export default CompetitionContractNoActivePlan;